import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { cleanName } from "../components/PlayerCard";
import { EditionTabs, useEditions } from "../components/EditionTabs";

export default function Leaderboard() {
  const [players, setPlayers] = useState([]);
  const { editions, selected, setCurrent } = useEditions();

  useSeo({
    title: "Hall of Fame Top 20 — KickStartKenyaFootball",
    description:
      "The Kenyan Top 20 ranked by League Strength (25), Prestige (20), Titles Won (20) and Impact (15) for a maximum score of 80.",
    image: cardUrl("hall-of-fame"),
  });

  useEffect(() => {
    if (!selected) return;
    api.get(`/leaderboard?edition=${selected}`).then((r) => setPlayers(r.data));
  }, [selected]);

  const max = 80;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="leaderboard-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Link to="/hall-of-fame" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 hover:text-ochre" data-testid="leaderboard-back">
          <ArrowLeft size={14} /> All players
        </Link>
        <p className="overline mt-10 text-ochre">Scoring model</p>
        <h1 className="display-1 mt-5">Hall of Fame Top 20</h1>
        <p className="body-copy mt-6 max-w-2xl">
          Four weighted criteria, eighty points available. League Strength (25), Prestige (20), Titles Won (20) and
          Impact (15). The scores are our own research-based estimates rather than official ratings — the model is
          published so it can be argued with, and it will be revised as careers develop and new players emerge.
        </p>
        <p className="mono mt-6 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-300" data-testid="leaderboard-reviewed">
          Reviewed August 2026 · scored on completed careers, not current form. This is why the order barely
          moves year to year: a Champions League medal or a national scoring record is finished history. New
          entries and re-scores arrive with each edition.
        </p>

        <EditionTabs editions={editions} selected={selected} onSelect={setCurrent} />

        <div className="mt-16 divide-y divide-zinc-900 border-t border-zinc-900" data-testid="leaderboard-list">
          {players.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.03, 0.3)}>
              <Link
                to={`/hall-of-fame/${p.slug}`}
                data-testid={`leaderboard-row-${p.slug}`}
                className="group grid grid-cols-[40px_48px_1fr] items-center gap-4 py-6 md:grid-cols-[64px_56px_1fr_180px_90px] md:gap-5"
              >
                <span className="font-display text-2xl font-bold leading-none text-zinc-700 transition-colors group-hover:text-ochre md:text-4xl">
                  {String(p.rank).padStart(2, "0")}
                </span>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden bg-white/[0.04] font-display text-base font-bold text-white/50 md:h-14 md:w-14 md:text-lg">
                  {p.portrait_url ? (
                    <img
                      src={p.portrait_url}
                      alt={cleanName(p.name || "")}
                      loading="lazy"
                      data-testid={`leaderboard-portrait-${p.slug}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    cleanName(p.name || "")
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join("")
                  )}
                </span>
                <div>
                  <h2 className="font-display text-lg font-semibold uppercase tracking-tight text-white md:text-xl">{cleanName(p.name)}</h2>
                  <p className="mt-1 text-[0.68rem] uppercase tracking-[0.16em] text-zinc-500">
                    Total score {p.total_score} of 80
                  </p>
                  {p.value_usd ? (
                    <p
                      className="mono mt-1.5 text-[0.62rem] uppercase tracking-[0.14em] text-white/70"
                      data-testid={`leaderboard-value-${p.slug}`}
                    >
                      {p.value_usd} / {p.value_kes}
                      <span className="ml-2 text-zinc-500">
                        {p.value_estimated ? `estimated, ${p.value_as_at}` : `transfer fee, ${p.value_as_at}`}
                      </span>
                    </p>
                  ) : null}
                  {p.did_you_know ? (
                    <p
                      className="mt-2 max-w-xl text-xs leading-snug text-ochre/90 transition-colors group-hover:text-white"
                      data-testid={`leaderboard-fact-${p.slug}`}
                    >
                      <span className="mono mr-2 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                        Did you know
                      </span>
                      {p.did_you_know.headline}
                    </p>
                  ) : null}
                </div>
                <div className="col-span-2 md:col-span-1">
                  <div className="h-1.5 w-full bg-zinc-900">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(p.total_score / max) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full bg-maasai"
                    />
                  </div>
                  <div className="mt-2 flex gap-3 text-[0.6rem] uppercase tracking-wider text-zinc-400">
                    <span>L {p.scores?.league_strength}</span>
                    <span>P {p.scores?.prestige}</span>
                    <span>T {p.scores?.titles}</span>
                    <span>I {p.scores?.impact}</span>
                  </div>
                </div>
                <span className="col-span-2 font-display text-2xl font-bold text-ochre md:col-span-1 md:text-right md:text-3xl">
                  {p.total_score}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
